import { pick } from 'lodash-es'
import { createSelector } from 'reselect'

import { getOrganization } from 'common'

// Create a stable empty object reference for memoization
const EMPTY_ORGANIZATION = {}

// Input selector for the organization query result
const organizationQuerySelector = (state) => getOrganization.select()(state)

// Memoized selector to extract data or return stable empty object
export const currentOrganizationSelector = createSelector(
  organizationQuerySelector,
  (queryResult) => queryResult?.data || EMPTY_ORGANIZATION
)

const segmentSliceSelector = ({ segment }) => segment

export const shouldEnforceLogoSelector = createSelector(
  currentOrganizationSelector,
  ({ enforce_logo: enforceLogo }) => enforceLogo
)

export const ga4MeasurementIdSelector = createSelector(
  currentOrganizationSelector,
  ({ google_analytics_account: account }) => account?.ga4_measurement_id
)

export const fbTrackingCodeSelector = createSelector(
  currentOrganizationSelector,
  ({ meta_analytics_account: account }) => account?.pixel_tracking_code
)

export const isSingleSelector = createSelector(
  currentOrganizationSelector,
  ({ kind }) => kind === 'single'
)

export const isMultipleSelector = createSelector(
  currentOrganizationSelector,
  ({ kind }) => kind === 'multiple'
)

export const orgEventTypeSettingsSelector = createSelector(
  currentOrganizationSelector,
  (organization) => (
    pick(organization, 'create_org_event_type_permission')
  )
)

export const ownerCountryCodeSelector = createSelector(
  currentOrganizationSelector,
  ({ owner }) => owner.country_code
)

export const ownerEmailSelector = createSelector(
  currentOrganizationSelector,
  ({ owner }) => owner.email
)

export const ownerIdSelector = createSelector(
  currentOrganizationSelector,
  ({ owner }) => owner.id
)

export const orgIdSelector = createSelector(
  segmentSliceSelector,
  (segment) => segment.identify_options[1].calendly_organization_id
)

export const orgWorkflowsSettingsSelector = createSelector(
  currentOrganizationSelector,
  (organization) => (
    pick(organization, 'workflows_enabled_for')
  )
)

export const orgInvitationPermissionsSelector = createSelector(
  currentOrganizationSelector,
  (organization) => organization.invitation_permissions
)

export const orgVisibilitySettingsSelector = createSelector(
  currentOrganizationSelector,
  (organization) => (
    pick(organization, 'available_to_request_to_join')
  )
)

export const orgSessionSettingsSelector = createSelector(
  currentOrganizationSelector,
  ({ session_timeout_in: sessionTimeOutIn }) => sessionTimeOutIn
)

export const ownerNameSelector = createSelector(
  currentOrganizationSelector,
  (organization) => organization.owner.name
)

export const isScimConfiguredSelector = createSelector(
  currentOrganizationSelector,
  ({ scim_configuration: scim }) => scim?.state === 'enabled'
)

export const scimGroupMappingStateSelector = createSelector(
  currentOrganizationSelector,
  ({ scim_configuration: scim }) => scim?.group_mapping_state
)

export const isSmsDeactivatedSelector = createSelector(
  currentOrganizationSelector,
  (organization) => organization.sms_activated_for === 'no_one'
)

export const hasVerifiedDomainsSelector = createSelector(
  currentOrganizationSelector,
  (organization) => organization.has_any_verified_domains
)

export const hasEnforcedDomainsSelector = createSelector(
  currentOrganizationSelector,
  (organization) => organization.has_any_enforced_domains
)

export const isIndividualOrganizationSelector = createSelector(
  currentOrganizationSelector,
  (organization) => organization.individual_organization
)

export const currentOrganizationNameSelector = createSelector(
  currentOrganizationSelector,
  (organization) => organization && organization.name
)

export const isNotificationCustomizationRestrictedSelector = createSelector(
  currentOrganizationSelector,
  (organization) => organization.notification_customization_restricted === true
)

export const smsCustomizationRestrictedSelector = createSelector(
  currentOrganizationSelector,
  (organization) => organization.sms_customization_restricted
)
